import { useCallback, useMemo, useState } from 'react';

import { Gtm } from '@/domains/core/tracking/utils/Gtm';
import { playlistFilterClickGtmEvent } from '@/domains/productDiscovery/Recommendation/components/Playlists/components/PlaylistFilter/gtmEvents';
import type { Product } from '@/domains/productDiscovery/Recommendation/interfaces/product';

interface UseBrandsFilterProps {
  products: Product[];
  playlistName?: string;
}

export interface BrandInfo {
  brandName: string;
  brandImageFullpath: string;
  brandId: number;
  count: number;
}

export interface UseBrandsFilter {
  brands: BrandInfo[];
  selectBrand: (brandName: string | null) => void;
  filteredProductsByBrand: Product[];
  selectedBrand: null | string;
}

const BRANDS_OPTIONS_COUNT = 3;

export function normalizeString(str: string) {
  const words = str.trim().split(' ');

  return words
    .map(
      (word) => `${word.charAt(0).toUpperCase()}${word.slice(1).toLowerCase()}`,
    )
    .join(' ');
}

export const getTopBrandsInRecommendation = (products: Product[]) => {
  const brandsCount = products.reduce<Record<string, BrandInfo>>(
    (accum, { brandName, brandImageFullpath, brandId }) => {
      if (brandId) {
        if (accum[brandId]) {
          return {
            ...accum,
            [brandId]: {
              ...accum[brandId],
              count: accum[brandId].count + 1,
              brandName: accum[brandId].brandName || normalizeString(brandName),
            },
          };
        }

        return {
          ...accum,
          [brandId]: {
            brandName: normalizeString(brandName),
            brandImageFullpath,
            brandId,
            count: 1,
          },
        };
      }
      return accum;
    },
    {} as Record<string, BrandInfo>,
  );

  return Object.values(brandsCount)
    .filter((brand) => brand.brandName !== '')
    .sort((a, b) => b.count - a.count)
    .slice(0, BRANDS_OPTIONS_COUNT);
};

export const useBrandsFilter = ({
  products,
  playlistName,
}: UseBrandsFilterProps): UseBrandsFilter => {
  const [selectedBrand, setSelectedBrand] = useState<string | null>(null);

  const brands = useMemo(
    () => getTopBrandsInRecommendation(products),
    [products],
  );

  const filteredProductsByBrand = useMemo(() => {
    if (!selectedBrand) {
      return products;
    }

    return products.filter(
      (product) =>
        product.brandName.toUpperCase() === selectedBrand.toUpperCase(),
    );
  }, [selectedBrand, products]);

  const selectBrand = useCallback(
    (brandName: string | null) => {
      if (brandName !== selectedBrand || brandName === null) {
        setSelectedBrand(brandName);

        if (brandName) {
          const brandIndex = brands.findIndex(
            (brand) => brand.brandName === brandName,
          );
          Gtm.push(
            playlistFilterClickGtmEvent({
              itemName: brandName,
              position: brandIndex + 1,
              count: brands[brandIndex].count,
              playlistName: playlistName ?? '',
            }),
          );
        }
      }
    },
    [brands, playlistName, selectedBrand],
  );

  return {
    brands,
    filteredProductsByBrand,
    selectBrand,
    selectedBrand,
  };
};
